"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObjectNode = exports.Case = exports.Switch = void 0;
const Node_1 = require("./Node");
const Path_1 = require("../model/Path");
const Errors_1 = require("../model/Errors");
const utils_1 = require("../utils");
const DataModel_1 = require("../model/DataModel");
exports.Switch = Symbol('switch');
exports.Case = Symbol('case');
const ObjectNode = (fields, config) => {
    const { [exports.Switch]: filter, [exports.Case]: cases, ...defaultFields } = fields;
    const getActiveFields = (path) => {
        if (filter === undefined)
            return defaultFields;
        const switchValue = (0, Path_1.relativePath)(path, filter).get();
        const activeCase = cases[switchValue];
        return { ...defaultFields, ...activeCase };
    };
    const getChildModelPath = (path, childKey) => {
        var _a;
        const switchValue = filter ? (0, Path_1.relativePath)(path, filter).get() : undefined;
        const caseFields = filter ? ((_a = cases[switchValue]) !== null && _a !== void 0 ? _a : {}) : {};
        const caseKeys = Object.keys(caseFields);
        const pathWithContext = (config === null || config === void 0 ? void 0 : config.context) ?
            new Path_1.ModelPath(path.getModel(), new Path_1.Path(path.getArray(), [config.context])) : path;
        const pathWithFilter = !(config === null || config === void 0 ? void 0 : config.disableSwitchContext) && switchValue && caseKeys.includes(childKey) ?
            pathWithContext.contextPush(switchValue) : pathWithContext;
        return pathWithFilter.push(childKey);
    };
    return ({
        ...Node_1.Base,
        type: () => 'object',
        category: () => config === null || config === void 0 ? void 0 : config.category,
        default: () => ({}),
        keep() {
            return this.optional();
        },
        navigate(path, index) {
            const nextIndex = index + 1;
            const pathElements = path.getArray();
            if (pathElements.length <= nextIndex) {
                return this;
            }
            const activeFields = getActiveFields(path.slice(0, nextIndex));
            const node = activeFields[pathElements[nextIndex]];
            return node === null || node === void 0 ? void 0 : node.navigate(path, nextIndex);
        },
        pathPush(path, key) {
            return getChildModelPath(path, key.toString());
        },
        suggest(path, value) {
            const activeFields = getActiveFields(path);
            const existingKeys = Object.keys(typeof value === 'object' ? value : {});
            return Object.keys(activeFields)
                .filter(k => activeFields[k].enabled(path))
                .filter(k => !existingKeys.includes(k))
                .map(utils_1.quoteString);
        },
        validate(path, value, errors, options) {
            if (options.loose && typeof value !== 'object') {
                value = options.wrapLists ? DataModel_1.DataModel.wrapLists(this.default()) : this.default();
            }
            if (typeof value !== 'object' || value === null) {
                errors.add(path, 'error.expected_object');
                return value;
            }
            let activeFields = defaultFields;
            if (filter) {
                const filterPath = (0, Path_1.relativePath)(path, filter);
                let switchValue = filterPath.get();
                if (path.equals(filterPath.pop())) {
                    const filterField = filterPath.last();
                    switchValue = defaultFields[filterField].validate(path.push(filterField), value[filterField], new Errors_1.Errors(), options);
                }
                activeFields = { ...activeFields, ...cases[switchValue] };
            }
            const activeKeys = Object.keys(activeFields);
            const forcedKeys = activeKeys.filter(k => !activeFields[k].optional());
            const keys = new Set([...forcedKeys, ...Object.keys(value)]);
            const res = {};
            keys.forEach(k => {
                if (activeKeys.includes(k)) {
                    if (!activeFields[k].enabled(path))
                        return;
                    const newValue = activeFields[k].validate(path.push(k), value[k], errors, options);
                    if (!activeFields[k].keep() && activeFields[k].optional()
                        && (newValue === undefined
                            || (Array.isArray(newValue) && newValue.length === 0)
                            || (newValue.constructor === Object && Object.keys(newValue).length === 0))) {
                        delete res[k];
                    }
                    else {
                        res[k] = newValue;
                        path.getModel().set(path.push(k), newValue, true);
                    }
                }
                else {
                    res[k] = value[k];
                }
            });
            for (const a of Object.getOwnPropertySymbols(value)) {
                res[a] = value[a];
            }
            return res;
        },
        hook(hook, path, ...args) {
            var _a;
            return ((_a = hook.object) !== null && _a !== void 0 ? _a : hook.base).call(hook, { node: this, fields: defaultFields, config: config !== null && config !== void 0 ? config : {}, filter, cases, getActiveFields, getChildModelPath }, path, ...args);
        }
    });
};
exports.ObjectNode = ObjectNode;
